/** @jsxImportSource @emotion/react */
import React from 'react';
import type { PlanCell, StructuralObject } from '../../../types/plan';
import type { PlanAppMode, PlanLayerVisibility } from '../../../types/planManagement';
import type { AllowedBoundingBox } from '../../../types/plan';
import type { EntityInteractionState } from '../../../hooks/useEntityInteraction';
import type { EntityLookup } from '../../../utils/planEntities';
import type { PlotGridLayers } from '../../../types/bulkOperations';
import PlanGridViewport from './PlanGridViewport';
import { planEditorStyles as styles } from './planEditorStyles';
import type { BulkPlacementPreview, GridSelectionRect } from './types';

interface PlanEditorWorkspaceProps {
  appMode: PlanAppMode;
  isPlotViewport: boolean;
  modeToolbar: React.ReactNode;
  managementPanel: React.ReactNode;
  entityHintText: string;
  entityState: EntityInteractionState;
  allowedBoundingBox: AllowedBoundingBox;
  cellMap: Map<string, PlanCell>;
  structuralObjects?: StructuralObject[];
  isSelectedCell: (row: number, col: number) => boolean;
  highlightCells?: Set<string>;
  entityLookup: EntityLookup;
  multiSelectedBeds: Set<string>;
  multiSelectedPlots: Set<string>;
  bedsWithPlantings: Set<string>;
  layerData?: PlotGridLayers;
  layerVisibility?: PlanLayerVisibility;
  onCellMouseDown: (row: number, col: number, e: React.MouseEvent<HTMLDivElement>) => void;
  onCellMouseEnter: (row: number, col: number) => void;
  onCellMouseLeave: () => void;
  onCellMouseUp: () => void;
  onGridMouseLeave: () => void;
  onGridScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  gridRef: (node: HTMLDivElement | null) => void;
  planningSelectionRect: GridSelectionRect | null;
  planningSelectedCells?: Set<string>;
  bulkPlacementPreview: BulkPlacementPreview | null;
  isEraseMode: boolean;
  allowBedSelection: boolean;
  isTransplantTargetMode: boolean;
}

const PlanEditorWorkspace: React.FC<PlanEditorWorkspaceProps> = (props) => {
  const baseGridProps = {
    isPlotViewport: props.isPlotViewport,
    entityHintText: props.entityHintText,
    showEntityHint: Boolean(props.entityState.hoveredEntity),
    allowedBoundingBox: props.allowedBoundingBox,
    cellMap: props.cellMap,
    structuralObjects: props.structuralObjects,
    isSelectedCell: props.isSelectedCell,
    highlightCells: props.highlightCells,
    entityState: props.entityState,
    entityLookup: props.entityLookup,
    multiSelectedBeds: props.multiSelectedBeds,
    multiSelectedPlots: props.multiSelectedPlots,
    bedsWithPlantings: props.bedsWithPlantings,
    layerData: props.layerData,
    layerVisibility: props.layerVisibility,
    onCellMouseDown: props.onCellMouseDown,
    onCellMouseEnter: props.onCellMouseEnter,
    onCellMouseLeave: props.onCellMouseLeave,
    onCellMouseUp: props.onCellMouseUp,
    onGridMouseLeave: props.onGridMouseLeave,
    onGridScroll: props.onGridScroll,
    gridRef: props.gridRef,
  };

  if (props.appMode === 'management') {
    return (
      <div css={styles.managementLayout}>
        <div css={styles.managementGrid}>
          <PlanGridViewport
            {...baseGridProps}
            selectionRect={null}
            appMode={props.appMode}
            isEraseMode={false}
            allowBedSelection={false}
            isTransplantTargetMode={props.isTransplantTargetMode}
            planningSelectedCells={undefined}
            bulkPlacementPreview={null}
          />
        </div>
        <div css={styles.managementSidebar}>
          {props.modeToolbar}
          {props.managementPanel}
        </div>
      </div>
    );
  }

  return (
    <div css={props.isPlotViewport ? styles.workspaceLayout : undefined}>
      <div css={props.isPlotViewport ? styles.workspaceGrid : undefined}>
        {!props.isPlotViewport && props.modeToolbar}
        <PlanGridViewport
          {...baseGridProps}
          selectionRect={props.planningSelectionRect}
          appMode={props.appMode}
          isEraseMode={props.isEraseMode}
          allowBedSelection={props.allowBedSelection}
          isTransplantTargetMode={props.isTransplantTargetMode}
          planningSelectedCells={props.planningSelectedCells}
          bulkPlacementPreview={props.bulkPlacementPreview}
        />
      </div>
      {props.isPlotViewport && <div css={styles.workspaceSidebar}>{props.modeToolbar}</div>}
    </div>
  );
};

export default PlanEditorWorkspace;
